/**
 * Assignees Page
 *
 * Browse tasks organized by assignee.
 */

import { useState, useMemo } from 'react';
import { User, Users, ChevronRight } from 'lucide-react';
import clsx from 'clsx';
import { useTasks } from '@/hooks/use-tasks';
import { TaskList } from '@/components/tasks';
import { Card, CardContent } from '@/components/ui';
import type { Task } from '@/types';

interface AssigneeStats {
  name: string;
  id: string | null;
  tasks: Task[];
  high: number;
  medium: number;
  low: number;
  pending: number;
  completed: number;
}

export function AssigneesPage() {
  const [selectedAssignee, setSelectedAssignee] = useState<string | null>(null);

  // Fetch all tasks
  const { data: allTasksData, isLoading: allLoading, error } = useTasks({});

  // Fetch filtered tasks when an assignee is selected
  const { data: filteredData, isLoading: filteredLoading } = useTasks(
    selectedAssignee !== null ? { assignee: selectedAssignee } : {}
  );

  const isLoading = allLoading || (selectedAssignee !== null && filteredLoading);
  const allTasks = allTasksData?.tasks ?? [];

  // Build assignee statistics
  const assigneeStats = useMemo(() => {
    const stats = new Map<string, AssigneeStats>();

    for (const task of allTasks) {
      const assigneeId = task.assigneeId;
      const name = assigneeId || 'Unassigned';
      const key = assigneeId || '__unassigned__';

      if (!stats.has(key)) {
        stats.set(key, {
          name,
          id: assigneeId || null,
          tasks: [],
          high: 0,
          medium: 0,
          low: 0,
          pending: 0,
          completed: 0,
        });
      }

      const stat = stats.get(key)!;
      stat.tasks.push(task);

      // Count by priority
      if (task.priority === 'high') stat.high++;
      else if (task.priority === 'medium') stat.medium++;
      else if (task.priority === 'low') stat.low++;

      // Count by status
      if (task.status === 'completed') {
        stat.completed++;
      } else {
        stat.pending++;
      }
    }

    return Array.from(stats.values()).sort((a, b) => {
      // Unassigned goes last
      if (!a.id) return 1;
      if (!b.id) return -1;
      // Sort by task count descending
      return b.tasks.length - a.tasks.length;
    });
  }, [allTasks]);

  const displayedTasks = selectedAssignee !== null
    ? (filteredData?.tasks ?? [])
    : allTasks;

  if (error) {
    return (
      <div className="p-4 md:p-6">
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-4">
            <p className="text-red-800">Failed to load assignees. Please try again.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Users className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Assignees</h1>
            <p className="text-gray-600 mt-1">
              View tasks by team member
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Sidebar - Assignee List */}
        <div className="lg:col-span-4 xl:col-span-3">
          <Card>
            <div className="p-3 border-b border-gray-200 bg-gray-50">
              <h3 className="font-semibold text-gray-900">Team Members</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {assigneeStats.length} assignee{assigneeStats.length !== 1 ? 's' : ''}
              </p>
            </div>

            {allLoading ? (
              <div className="p-4 animate-pulse space-y-2">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-16 bg-gray-200 rounded-lg" />
                ))}
              </div>
            ) : (
              <div className="p-2 max-h-[500px] overflow-y-auto">
                {/* All Assignees option */}
                <button
                  onClick={() => setSelectedAssignee(null)}
                  className={clsx(
                    'w-full flex items-center gap-3 p-3 rounded-lg transition-colors text-left mb-2',
                    selectedAssignee === null
                      ? 'bg-primary-100 text-primary-700'
                      : 'hover:bg-gray-100'
                  )}
                >
                  <div className="p-2 bg-primary-500 rounded-full">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900">All Assignees</p>
                    <p className="text-sm text-gray-500">
                      {allTasks.length} tasks total
                    </p>
                  </div>
                </button>

                {assigneeStats.length > 0 && (
                  <div className="border-t border-gray-200 pt-2 mt-2">
                    {assigneeStats.map((assignee) => (
                      <button
                        key={assignee.id || '__unassigned__'}
                        onClick={() => setSelectedAssignee(assignee.id || 'unassigned')}
                        className={clsx(
                          'w-full flex items-center gap-3 p-3 rounded-lg transition-colors text-left',
                          selectedAssignee === (assignee.id || 'unassigned')
                            ? 'bg-primary-100 text-primary-700'
                            : 'hover:bg-gray-100'
                        )}
                      >
                        <div
                          className={clsx(
                            'p-2 rounded-full',
                            !assignee.id ? 'bg-gray-300' : 'bg-blue-500'
                          )}
                        >
                          <User className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p
                            className={clsx(
                              'font-medium',
                              !assignee.id ? 'text-gray-500 italic' : 'text-gray-900'
                            )}
                          >
                            {assignee.name}
                          </p>
                          <div className="flex items-center gap-2 text-xs mt-0.5">
                            <span className="text-gray-500">
                              {assignee.tasks.length} tasks
                            </span>
                            {assignee.high > 0 && (
                              <span className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded">
                                {assignee.high} high
                              </span>
                            )}
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Card>
        </div>

        {/* Main - Task List */}
        <div className="lg:col-span-8 xl:col-span-9">
          <Card>
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {selectedAssignee === null
                  ? 'All Assignees'
                  : selectedAssignee === 'unassigned'
                  ? 'Unassigned'
                  : selectedAssignee}
              </h2>
              <p className="text-sm text-gray-500">
                {displayedTasks.length} task{displayedTasks.length !== 1 ? 's' : ''}
              </p>
            </div>
            <CardContent className="p-0">
              <TaskList
                tasks={displayedTasks}
                isLoading={!!isLoading}
                emptyMessage={
                  selectedAssignee
                    ? `No tasks for ${selectedAssignee === 'unassigned' ? 'unassigned' : selectedAssignee}`
                    : 'No tasks found'
                }
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}





